import type { MessageStatus } from "./api";

export const STATUS_META: Record<MessageStatus, { label: string; cls: string }> = {
  draft: { label: "Draft", cls: "bg-muted text-foreground/80 border-border" },
  approved: { label: "Approved", cls: "bg-[#FF5C00]/10 text-[#C24600] dark:text-[#FF8A47] border-[#FF5C00]/30" },
  sending: { label: "Sending", cls: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30" },
  paused: { label: "Paused", cls: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  cancelled: { label: "Cancelled", cls: "bg-muted text-muted-foreground border-border line-through" },
  completed: { label: "Completed", cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30" },
  needs_attention: { label: "Needs attention", cls: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30" },
};

export function StatusPill({ status }: { status: string }) {
  const m = STATUS_META[status as MessageStatus] || { label: status, cls: "bg-muted text-muted-foreground border-border" };
  return <span data-testid={`status-${status}`} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

export function fmtDate(s: string | null | undefined, withTime = true) {
  if (!s) return "Not set";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("en-GB", withTime ? { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" } : { day: "numeric", month: "short", year: "numeric" });
}

export function Eyebrow({ children }: { children: React.ReactNode }) {
  return <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{children}</p>;
}

export function Panel({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <section className={`rounded-xl border border-border bg-card text-card-foreground ${className}`}>{children}</section>;
}

export function Notice({ tone = "info", children }: { tone?: "info" | "warn" | "error" | "ok"; children: React.ReactNode }) {
  const cls = {
    info: "border-border bg-muted/50 text-foreground/80",
    warn: "border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-200",
    error: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
    ok: "border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200",
  }[tone];
  return <div role={tone === "error" ? "alert" : undefined} className={`rounded-lg border px-3 py-2 text-sm leading-relaxed ${cls}`}>{children}</div>;
}

export function Skeleton({ rows = 3 }: { rows?: number }) {
  return <div className="space-y-2">{Array.from({ length: rows }).map((_, i) => <div key={i} className="h-14 rounded-lg bg-muted animate-pulse" />)}</div>;
}

export const inputCls = "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#FF5C00]/40";
export const btnPrimary = "inline-flex items-center justify-center gap-2 rounded-lg bg-[#FF5C00] px-4 py-2 text-sm font-semibold text-white hover:bg-[#E55200] disabled:opacity-50 disabled:cursor-not-allowed";
export const btnGhost = "inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed";

export function parseEmails(raw: string): string[] {
  return Array.from(new Set(raw.split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean)));
}
export const looksLikeEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
